import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { TicketData, TicketDataTableItem } from '@/common/types/support/ticket.type'
import SupportTicketsResumeList from '@/features/support/components/ResumeList'
import { useSupportReasons } from '@/features/support/hooks/useSupportReasons'
import { useSupportTicketsTotalizer } from '@/features/support/hooks/useSupportTicketsTotalizer'
import { defaultColumns } from '@/features/support/internal/constants/columns.constant'
import { supportStatusOptions } from '@/features/support/internal/constants/supportStatusOptions.constant'
import { useSupportTickets } from '@/features/support/internal/hooks/useSupportTickets'
import { InternalSupportTicketsTransformer } from '@/features/support/internal/services/internalSupportTicketsTransformer'
import { SupportTicketTotalizerData } from '@/features/support/types/supportTicketTotalizer.type'
import { SearchIcon, TicketIcon } from 'lucide-react'

const InternalSupportList = () => {
  const { filterParams, setFilterParams } = useFilterParams()
  const { date, formattedDate } = useFilterDate(filterParams)

  const [searchName, setSearchName] = useDebounce(filterParams.name || '')
  const [searchSupportReason, setSearchSupportReason] = useDebounce('')

  const {
    data: supportTickets,
    meta,
    isLoading: supportTicketsIsLoading,
    error: supportTicketsError = {} as any,
    refetch: refetchSupportTickets,
  } = useSupportTickets({
    filters: {
      page: filterParams.page || 1,
      search: searchName,
      status: filterParams.status?.value || '',
      reason_id: filterParams.reason_id?.value || '',
      kind: 'internal',
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to
    },
    mapper: Mapper.mapWithTransformer<TicketData[], TicketDataTableItem[]>({
      transformer: InternalSupportTicketsTransformer.tableTransformer(),
    }),
  })

  const { data: supportTicketTotalizer } = useSupportTicketsTotalizer({
    filters: {
      page: filterParams.page || 1,
      search: searchName,
      status: filterParams.status?.value || '',
      reason_id: filterParams.reason_id?.value || '',
      kind: 'internal',
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to
    },
    mapper: Mapper.default<SupportTicketTotalizerData>(),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToListSupportTicketsTotalizers =
    isUserPermissionsPending || hasUserPermission('Admin::Support::Tickets::Totalizer')

  const { data: supportReasonOptions } = useSupportReasons({
    filters: { search: searchSupportReason },
    mapper: Mapper.mapToOptions({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  return (
    <div>
      <div className="flex flex-col gap-6">
        <ListHeader
          Icon={<TicketIcon className="size-10" strokeWidth={1.5} />}
          title="Chamados Internos"
          createButtonText="Abrir chamado"
          dataTestid="new_support_ticket_button"
          operationKlassToCheckPermission="Admin::Support::Tickets::Create"
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-128px)]"
          statusCode={supportTicketsError?.status}
          refetchFunction={refetchSupportTickets}
        >
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <TextField
                name="filter_search"
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                value={filterParams.name || ''}
                onChange={(e: any) => {
                  setSearchName(e.target?.value)
                  setFilterParams((prev) => ({
                    ...prev,
                    name: e.target?.value,
                  }))
                }}
              />
              <div className='flex flex-wrap gap-2 items-center'>
                <DatePickerWithRange
                  placeholder="Período do chamado"
                  date={date}
                  setDate={(value) => setFilterParams((prev) => ({ ...prev, date: value }))}
                />
                
                <ListFilters
                  filters={[
                    {
                      className: 'min-w-[120px]',
                      onChange: (e: any) =>
                        setFilterParams((params) => ({
                          ...params,
                          status: e.value ? e : undefined,
                        })),
                      value: filterParams.status || undefined,
                      options: [{ label: 'Todos', value: '' }, ...(supportStatusOptions || [])],
                      placeholder: 'Status',
                      dataTestid: 'filter_status',
                    },
                    {
                      className: 'min-w-[120px]',
                      onChange: (e: any) =>
                        setFilterParams((params) => ({
                          ...params,
                          reason_id: e.value ? e : undefined,
                        })),
                      onInputChange: setSearchSupportReason,
                      value: filterParams.reason_id || undefined,
                      options: [{ label: 'Todos', value: '' }, ...(supportReasonOptions || [])],
                      placeholder: 'Motivo',
                      dataTestid: 'filter_reason',
                    },
                  ]}
                />
              </div>
            </div>
            {userHasPermissionToListSupportTicketsTotalizers && (
              <SupportTicketsResumeList data={supportTicketTotalizer} kind='internal' />
            )}

            <Table
              className="max-h-screen"
              columns={defaultColumns}
              isLoadingData={supportTicketsIsLoading}
              page={filterParams.page ? Number(filterParams.page) : 1}
              rowEmptyValue="--"
              rows={supportTickets}
              totalPages={meta.total_pages}
              totalCount={meta.total}
              hasPagination
              setPage={(p) => setFilterParams((params) => ({ ...params, page: p }))}
            />
          </div>
        </ErrorMessageWrapper>
      </div>
    </div>
  )
}

export default InternalSupportList
